import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import '../App.css';

const Register: React.FC = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    profileType: 'player',
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    phoneNumber: '',
    address: '',
    soccerExperience: '',
    profilePicture: ''
  });
  const [profilePicturePreview, setProfilePicturePreview] = useState<string>('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [error, setError] = useState('');
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file');
        return;
      }
      
      // Validate file size (max 2MB to keep base64 encoding reasonable)
      // Base64 encoding increases size by ~33%, so 2MB file becomes ~2.67MB base64
      if (file.size > 2 * 1024 * 1024) {
        setError('Image size must be less than 2MB');
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result as string;
        setFormData({
          ...formData,
          profilePicture: base64String
        });
        setProfilePicturePreview(base64String);
        setError('');
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!formData.profilePicture) {
      setError('Profile picture is required');
      return;
    }

    // Soccer experience is required for players
    if (formData.profileType === 'player' && !formData.soccerExperience) {
      setError('Soccer experience is required for players');
      return;
    }

    if (!agreedToTerms) {
      setError('You must agree to the Terms & Conditions to register');
      return;
    }

    try {
      await register(formData);
      const redirect = searchParams.get('redirect');
      if (redirect && redirect.startsWith('/guest-invite/')) {
        navigate(redirect);
      } else if (formData.profileType === 'player') {
        navigate('/players/select-team');
      } else {
        navigate('/dashboard');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Registration failed');
    }
  };

  return (
    <div className="container" style={{ maxWidth: '500px', marginTop: '50px' }}>
      <div className="card">
        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          <img src="/pp-logo.png" alt="Pitch Pilot" style={{ height: '80px', objectFit: 'contain' }} />
          <h2 style={{ marginTop: '10px' }}>Create Account</h2>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Email *</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-group">
            <label>Password *</label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-group">
            <label>Profile Type *</label>
            <select
              name="profileType"
              value={formData.profileType}
              onChange={handleChange}
              required
            >
              <option value="player">Player</option>
              <option value="referee">Referee</option>
              <option value="parent">Parent</option>
            </select>
            <p style={{ fontSize: '12px', color: '#757575', marginTop: '5px' }}>
              Note: Manager, Director, and Admin roles can only be assigned by existing admins.
            </p>
          </div>
          <div className="form-group">
            <label>First Name *</label>
            <input
              type="text"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-group">
            <label>Last Name *</label>
            <input
              type="text"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-group">
            <label>Profile Picture *</label>
            <input
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              required
            />
            {profilePicturePreview && (
              <div style={{ marginTop: '10px' }}>
                <img 
                  src={profilePicturePreview} 
                  alt="Profile preview" 
                  style={{ width: '100px', height: '100px', borderRadius: '50%', objectFit: 'cover' }}
                />
              </div>
            )}
          </div>
          <div className="form-group">
            <label>Date of Birth</label>
            <input
              type="date"
              name="dateOfBirth"
              value={formData.dateOfBirth}
              onChange={handleChange}
            />
          </div>
          <div className="form-group">
            <label>Phone Number</label>
            <input
              type="tel"
              name="phoneNumber"
              value={formData.phoneNumber}
              onChange={handleChange}
            />
          </div>
          <div className="form-group">
            <label>Address</label>
            <input
              type="text"
              name="address"
              value={formData.address}
              onChange={handleChange}
            />
          </div>
          {formData.profileType === 'player' && (
            <div className="form-group">
              <label>Soccer Experience *</label>
              <select
                name="soccerExperience"
                value={formData.soccerExperience}
                onChange={handleChange}
                required
              >
                <option value="">Select Experience Level</option>
                <option value="Beginner">Beginner</option>
                <option value="Recreational">Recreational</option>
                <option value="Intermediate">Intermediate</option>
                <option value="Advanced">Advanced</option>
                <option value="Competitive">Competitive</option>
                <option value="Elite">Elite</option>
              </select>
            </div>
          )}
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={agreedToTerms}
                onChange={(e) => setAgreedToTerms(e.target.checked)}
                style={{ width: '20px', height: '20px', marginTop: '2px', cursor: 'pointer', flexShrink: 0 }}
              />
              <span style={{ fontSize: '14px', color: '#424242' }}>
                I have read and agree to the{' '}
                <Link to="/terms" target="_blank" style={{ color: '#2E7D32', fontWeight: '500' }}>
                  Terms & Conditions
                </Link>
                {' '}*
              </span>
            </label>
          </div>

          {error && <div className="error">{error}</div>}
          <button type="submit" className="btn btn-primary" style={{ width: '100%' }} disabled={!agreedToTerms}>
            Register
          </button>
        </form>
        <p style={{ marginTop: '15px', textAlign: 'center' }}>
          Already have an account? <a href="/login">Login</a>
        </p>
      </div>
    </div>
  );
};

export default Register;

